import React from "react";

import {
  Chip,
  Stack,
  TableCell,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

import { useNavigate } from "react-router-dom";

import UserChip from "../../../components/chips/user-chip";
import RoleChip from "../../../components/chips/role-chip";
import OrganisationChip from "../../../components/chips/team-chip";
import { Organisation, User } from "../../../types";
import { getTeamRoleInfo } from "../../../utils/get-team-role-info";

interface TeamsTableRowProps {
  team: Organisation;
  users: Record<string, User>;
  showManageButton?: boolean;
}

const TeamsTableRow: React.FC<TeamsTableRowProps> = ({
  team,
  users,
  showManageButton = true,
}) => {
  const filteredUsers =
    team.usersIds
      ?.map((userId) => users[userId])
      .filter((user): user is User => Boolean(user)) || [];

  const navigate = useNavigate();

  const navigateToPath = (navigationPath: string) => {
    navigate(navigationPath);
  };

  const teamDetailsPath = `/team?teamId=${team.organisationId}`;

  return (
    <TableRow
      key={team.organisationId}
      sx={{
        cursor: showManageButton ? "pointer" : "default",
        ...(showManageButton
          ? {
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(5, 84, 128, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)",
              },
            }
          : {}),
      }}
      onClick={showManageButton ? () => navigateToPath(teamDetailsPath) : undefined}
    >
      <TableCell>
        <OrganisationChip
          teamId={team.organisationId}
          name={team.name}
          color={team.color}
          sx={{
            maxWidth: "100%",
          }}
        />
      </TableCell>
      <TableCell align="left">
        {!team.meetingViews || team.meetingViews.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem" }}
          >
            No Layouts
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {team.meetingViews.map((meetingView) => (
              <Chip
                key={meetingView}
                variant="outlined"
                label={meetingView.charAt(0).toUpperCase() + meetingView.slice(1)}
                size="small"
                sx={{ fontSize: "0.8rem" }}
              />
            ))}
          </Stack>
        )}
      </TableCell>
      <TableCell align="left">
        {filteredUsers.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem" }}
          >
            No Users
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {filteredUsers
              .map((user) => {
                return (
                  <UserChip
                    key={user.userId}
                    name={user.fullName || user.email || user.userId}
                    avatarColor="#64748b"
                    fullWidth={false}
                    roleChip={
                      <RoleChip
                        role={getTeamRoleInfo(user, team.organisationId)}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.58rem",
                          borderRadius: "999px",
                          "& .MuiChip-label": {
                            px: 0.75,
                          },
                        }}
                      />
                    }
                    sx={{
                      maxWidth: "100%",
                    }}
                  />
                );
              })}
          </Stack>
        )}
      </TableCell>
      <TableCell align="right">
        {showManageButton && (
          <Button
            variant="contained"
            size="small"
            startIcon={<SettingsIcon />}
            sx={{
              fontSize: "0.8rem",
              py: 0.5,
              borderRadius: 999,
            }}
            onClick={(event) => {
              event.stopPropagation();
              navigateToPath(teamDetailsPath);
            }}
          >
            Manage
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default TeamsTableRow;
